/** Dữ liệu mẫu để xem UI payroll / phiếu lương cá nhân.
 * Bật bằng `?mock=1` trên URL, hoặc tự dùng khi API trả về rỗng.
 */

const now = new Date();
const ym = (offsetMonths) => {
  const d = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const publishedAt = (offsetMonths, day = 5) => {
  const d = new Date(now.getFullYear(), now.getMonth() + offsetMonths, day, 9, 30, 0);
  return d.toISOString();
};

/** Bám theo rebuildPayslipItems ở backend: earning dương, deduction âm, info để tham chiếu. */
function makeItems(payslipId, row) {
  const overtimeHours = Number(row.overtime_minutes || 0) / 60;
  return [
    {
      id: `${payslipId}-i1`,
      payslip_id: payslipId,
      item_type: "earning",
      item_code: "base_salary",
      label: "Lương cơ sở",
      amount: row.base_salary,
      sort_order: 1,
    },
    {
      id: `${payslipId}-i2`,
      payslip_id: payslipId,
      item_type: "earning",
      item_code: "allowance",
      label: "Phụ cấp",
      amount: row.allowance,
      sort_order: 2,
    },
    {
      id: `${payslipId}-i3`,
      payslip_id: payslipId,
      item_type: "info",
      item_code: "actual_work_days",
      label: `Ngày công thực tế (${row.actual_work_days} / ${row.standard_work_days})`,
      amount: 0,
      sort_order: 3,
    },
    {
      id: `${payslipId}-i4`,
      payslip_id: payslipId,
      item_type: "earning",
      item_code: "overtime_amount",
      label: `Tiền tăng ca (${overtimeHours} giờ)`,
      amount: row.overtime_amount,
      sort_order: 4,
    },
    {
      id: `${payslipId}-i5`,
      payslip_id: payslipId,
      item_type: "deduction",
      item_code: "unpaid_leave_deduction",
      label: `Khấu trừ nghỉ không lương (${row.unpaid_leave_days} ngày)`,
      amount: -Math.abs(row.unpaid_leave_deduction),
      sort_order: 5,
    },
    {
      id: `${payslipId}-i6`,
      payslip_id: payslipId,
      item_type: "earning",
      item_code: "adjustment_plus",
      label: "Điều chỉnh cộng",
      amount: row.adjustment_plus,
      sort_order: 6,
    },
    {
      id: `${payslipId}-i7`,
      payslip_id: payslipId,
      item_type: "deduction",
      item_code: "adjustment_minus",
      label: "Điều chỉnh trừ",
      amount: -Math.abs(row.adjustment_minus),
      sort_order: 7,
    },
    {
      id: `${payslipId}-i8`,
      payslip_id: payslipId,
      item_type: "deduction",
      item_code: "manual_deduction",
      label: "Khấu trừ thủ công",
      amount: -Math.abs(row.manual_deduction),
      sort_order: 8,
    },
    {
      id: `${payslipId}-i9`,
      payslip_id: payslipId,
      item_type: "info",
      item_code: "gross_pay",
      label: "Tổng thu nhập",
      amount: row.gross_pay,
      sort_order: 9,
    },
    {
      id: `${payslipId}-i10`,
      payslip_id: payslipId,
      item_type: "info",
      item_code: "net_pay",
      label: "Thực lĩnh",
      amount: row.net_pay,
      sort_order: 10,
    },
  ];
}

const sampleEmployees = [
  {
    id: "mock-emp-1",
    name: "Nguyễn Văn An",
    department: "Phòng Kỹ thuật",
    position: "Nhân viên",
    bank: "Vietcombank",
    bankNo: "0123456789",
    base_salary: 12000000,
    allowance: 1500000,
  },
  {
    id: "mock-emp-2",
    name: "Trần Thị Bình",
    department: "Phòng Kinh doanh",
    position: "Chuyên viên",
    bank: "Techcombank",
    bankNo: "19036551234",
    base_salary: 15000000,
    allowance: 2000000,
  },
  {
    id: "mock-emp-3",
    name: "Lê Minh Cường",
    department: "Phòng Nhân sự",
    position: "Trưởng nhóm",
    bank: "MB Bank",
    bankNo: "9704221234567",
    base_salary: 18000000,
    allowance: 2500000,
  },
];

function buildPayslip(runId, yearMonth, emp, overrides = {}) {
  const standard = 22;
  const actual = overrides.actual_work_days ?? 21;
  const overtime_amount = overrides.overtime_amount ?? 800000;
  const overtime_minutes = overrides.overtime_minutes ?? 480;
  const unpaid_leave_days = overrides.unpaid_leave_days ?? 1;
  const unpaid_leave_deduction = Math.round((emp.base_salary / standard) * unpaid_leave_days);
  const adjustment_plus = overrides.adjustment_plus ?? 200000;
  const adjustment_minus = overrides.adjustment_minus ?? 0;
  const manual_deduction = overrides.manual_deduction ?? 1050000;
  const gross_pay =
    emp.base_salary + emp.allowance + overtime_amount + adjustment_plus - unpaid_leave_deduction - adjustment_minus;
  const net_pay = gross_pay - manual_deduction;
  const payslipId = overrides.id || `mock-ps-${runId}-${emp.id}`;

  const row = {
    id: payslipId,
    payroll_run_id: runId,
    employee_id: emp.id,
    employee_name_snapshot: emp.name,
    department_name_snapshot: emp.department,
    branch_name_snapshot: "Chi nhánh Hà Nội",
    position_snapshot: emp.position,
    payroll_bank_name_snapshot: emp.bank,
    payroll_bank_number_snapshot: emp.bankNo,
    base_salary: emp.base_salary,
    allowance: emp.allowance,
    standard_work_days: standard,
    actual_work_days: actual,
    overtime_minutes,
    overtime_amount,
    unpaid_leave_days,
    unpaid_leave_deduction,
    adjustment_plus,
    adjustment_minus,
    manual_deduction,
    gross_pay,
    net_pay,
    note: overrides.note || "",
    status: overrides.status || "published",
    published_at: overrides.published_at || null,
    year_month: yearMonth,
    run_code: overrides.run_code,
  };

  return {
    ...row,
    items: makeItems(payslipId, row),
  };
}

const RUN_IDS = {
  draft: "mock-run-draft",
  pendingCoo: "mock-run-pending-coo",
  pendingCeo: "mock-run-pending-ceo",
  approved: "mock-run-approved",
  published: "mock-run-published",
  rejected: "mock-run-rejected",
};

function buildRunSummary(payslips) {
  return {
    employee_count: payslips.length,
    gross_total: payslips.reduce((s, p) => s + Number(p.gross_pay || 0), 0),
    net_total: payslips.reduce((s, p) => s + Number(p.net_pay || 0), 0),
    overtime_total: payslips.reduce((s, p) => s + Number(p.overtime_amount || 0), 0),
  };
}

function cloneDeep(value) {
  return JSON.parse(JSON.stringify(value));
}

/** Phiếu lương cá nhân đã công bố (2–3 tháng gần đây) */
export function getMockMyPayslips() {
  const months = [ym(-1), ym(-2), ym(-3)];
  return months.map((month, idx) => {
    const emp = sampleEmployees[0];
    const ps = buildPayslip(RUN_IDS.published, month, emp, {
      id: `mock-my-ps-${idx + 1}`,
      status: "published",
      published_at: publishedAt(-(idx + 1)),
      run_code: `PAY-${month.replace("-", "")}-ALL`,
      overtime_amount: 600000 + idx * 150000,
      overtime_minutes: 360 + idx * 60,
      adjustment_plus: idx === 0 ? 500000 : 0,
      note: idx === 0 ? "Thưởng hoàn thành KPI quý." : "",
    });
    return ps;
  });
}

function createInitialMockStore() {
  const monthDraft = ym(0);
  const monthCoo = ym(-1);
  const monthCeo = ym(-1);
  const monthApproved = ym(-2);
  const monthPublished = ym(-3);
  const monthRejected = ym(-2);

  const payslipsFor = (runId, yearMonth, status, published_at = null) =>
    sampleEmployees.map((emp, i) =>
      buildPayslip(runId, yearMonth, emp, {
        status,
        published_at,
        run_code: `PAY-${yearMonth.replace("-", "")}-ALL`,
        overtime_amount: 700000 + i * 100000,
        overtime_minutes: 420 + i * 60,
        note: i === 1 ? "Hỗ trợ đi công tác." : "",
      })
    );

  const details = {
    [RUN_IDS.draft]: {
      run: {
        id: RUN_IDS.draft,
        year_month: monthDraft,
        run_code: `PAY-${monthDraft.replace("-", "")}-ALL`,
        status: "draft",
        branch_name: "Toàn công ty",
        created_by_name: "Kế toán mẫu",
        submitted_at: null,
        published_at: null,
        current_approver_name: null,
      },
      payslips: payslipsFor(RUN_IDS.draft, monthDraft, "draft"),
      approvals: [
        { id: "a1", level: 1, approver_role: "COO", approver_name: "Phạm COO", status: "queued", acted_at: null, note: null },
        { id: "a2", level: 2, approver_role: "CEO", approver_name: "Hoàng CEO", status: "queued", acted_at: null, note: null },
      ],
    },
    [RUN_IDS.pendingCoo]: {
      run: {
        id: RUN_IDS.pendingCoo,
        year_month: monthCoo,
        run_code: `PAY-${monthCoo.replace("-", "")}-ALL`,
        status: "pending_coo",
        branch_name: "Toàn công ty",
        created_by_name: "Kế toán mẫu",
        submitted_at: publishedAt(-1, 2),
        published_at: null,
        current_approver_name: "Phạm COO",
      },
      payslips: payslipsFor(RUN_IDS.pendingCoo, monthCoo, "locked"),
      approvals: [
        { id: "b1", level: 1, approver_role: "COO", approver_name: "Phạm COO", status: "waiting", acted_at: null, note: null },
        { id: "b2", level: 2, approver_role: "CEO", approver_name: "Hoàng CEO", status: "queued", acted_at: null, note: null },
      ],
    },
    [RUN_IDS.pendingCeo]: {
      run: {
        id: RUN_IDS.pendingCeo,
        year_month: monthCeo,
        run_code: `PAY-${monthCeo.replace("-", "")}-B`,
        status: "pending_ceo",
        branch_name: "Chi nhánh Hà Nội",
        created_by_name: "Kế toán mẫu",
        submitted_at: publishedAt(-1, 3),
        published_at: null,
        current_approver_name: "Hoàng CEO",
      },
      payslips: payslipsFor(RUN_IDS.pendingCeo, monthCeo, "locked").slice(0, 2),
      approvals: [
        {
          id: "c1",
          level: 1,
          approver_role: "COO",
          approver_name: "Phạm COO",
          status: "approved",
          acted_at: publishedAt(-1, 4),
          note: null,
        },
        { id: "c2", level: 2, approver_role: "CEO", approver_name: "Hoàng CEO", status: "waiting", acted_at: null, note: null },
      ],
    },
    [RUN_IDS.approved]: {
      run: {
        id: RUN_IDS.approved,
        year_month: monthApproved,
        run_code: `PAY-${monthApproved.replace("-", "")}-ALL`,
        status: "approved",
        branch_name: "Toàn công ty",
        created_by_name: "Kế toán mẫu",
        submitted_at: publishedAt(-2, 2),
        published_at: null,
        current_approver_name: null,
      },
      payslips: payslipsFor(RUN_IDS.approved, monthApproved, "locked"),
      approvals: [
        {
          id: "d1",
          level: 1,
          approver_role: "COO",
          approver_name: "Phạm COO",
          status: "approved",
          acted_at: publishedAt(-2, 3),
          note: null,
        },
        {
          id: "d2",
          level: 2,
          approver_role: "CEO",
          approver_name: "Hoàng CEO",
          status: "approved",
          acted_at: publishedAt(-2, 4),
          note: null,
        },
      ],
    },
    [RUN_IDS.published]: {
      run: {
        id: RUN_IDS.published,
        year_month: monthPublished,
        run_code: `PAY-${monthPublished.replace("-", "")}-ALL`,
        status: "published",
        branch_name: "Toàn công ty",
        created_by_name: "Kế toán mẫu",
        submitted_at: publishedAt(-3, 2),
        published_at: publishedAt(-3, 5),
        current_approver_name: null,
      },
      payslips: payslipsFor(RUN_IDS.published, monthPublished, "published", publishedAt(-3, 5)),
      approvals: [
        {
          id: "e1",
          level: 1,
          approver_role: "COO",
          approver_name: "Phạm COO",
          status: "approved",
          acted_at: publishedAt(-3, 3),
          note: null,
        },
        {
          id: "e2",
          level: 2,
          approver_role: "CEO",
          approver_name: "Hoàng CEO",
          status: "approved",
          acted_at: publishedAt(-3, 4),
          note: null,
        },
      ],
    },
    [RUN_IDS.rejected]: {
      run: {
        id: RUN_IDS.rejected,
        year_month: monthRejected,
        run_code: `PAY-${monthRejected.replace("-", "")}-B`,
        status: "rejected",
        branch_name: "Chi nhánh Đà Nẵng",
        created_by_name: "Kế toán mẫu",
        submitted_at: publishedAt(-2, 2),
        published_at: null,
        current_approver_name: null,
      },
      payslips: payslipsFor(RUN_IDS.rejected, monthRejected, "draft").slice(0, 2),
      approvals: [
        {
          id: "f1",
          level: 1,
          approver_role: "COO",
          approver_name: "Phạm COO",
          status: "rejected",
          acted_at: publishedAt(-2, 3),
          note: "Số ngày công chưa khớp với bảng chốt công. Vui lòng kiểm tra lại.",
        },
        { id: "f2", level: 2, approver_role: "CEO", approver_name: "Hoàng CEO", status: "queued", acted_at: null, note: null },
      ],
    },
  };

  Object.values(details).forEach((detail) => {
    detail.summary = buildRunSummary(detail.payslips);
  });

  const runs = Object.values(details).map((detail) => ({
    id: detail.run.id,
    year_month: detail.run.year_month,
    run_code: detail.run.run_code,
    status: detail.run.status,
    branch_name: detail.run.branch_name,
    created_by_name: detail.run.created_by_name,
    current_approver_name: detail.run.current_approver_name,
    payslip_count: detail.payslips.length,
    net_total: detail.summary.net_total,
    created_at: detail.run.submitted_at || publishedAt(-1, 1),
  }));

  return { runs, details };
}

let mockStore = createInitialMockStore();

export function isPayrollMockForced() {
  try {
    return new URLSearchParams(window.location.search).get("mock") === "1";
  } catch {
    return false;
  }
}

export function resetPayrollMockStore() {
  mockStore = createInitialMockStore();
  return cloneDeep(mockStore);
}

export function getMockPayrollRuns(filters = {}) {
  let list = cloneDeep(mockStore.runs);
  if (filters.year_month) list = list.filter((r) => r.year_month === filters.year_month);
  if (filters.status) list = list.filter((r) => r.status === filters.status);
  return list;
}

export function getMockPayrollRunDetail(runId) {
  const detail = mockStore.details[runId];
  if (!detail) return null;
  return cloneDeep(detail);
}

export function mockUpdatePayslip(runId, payslipId, payload) {
  const detail = mockStore.details[runId];
  if (!detail) throw new Error("Không tìm thấy kỳ lương mẫu.");
  const row = detail.payslips.find((p) => p.id === payslipId);
  if (!row) throw new Error("Không tìm thấy phiếu lương mẫu.");

  row.adjustment_plus = Number(payload.adjustment_plus || 0);
  row.adjustment_minus = Number(payload.adjustment_minus || 0);
  row.manual_deduction = Number(payload.manual_deduction || 0);
  row.note = payload.note || "";
  row.gross_pay =
    Number(row.base_salary || 0) +
    Number(row.allowance || 0) +
    Number(row.overtime_amount || 0) +
    Number(row.adjustment_plus || 0) -
    Number(row.unpaid_leave_deduction || 0) -
    Number(row.adjustment_minus || 0);
  row.net_pay = row.gross_pay - Number(row.manual_deduction || 0);
  row.items = makeItems(row.id, row);

  detail.summary = buildRunSummary(detail.payslips);
  const runListItem = mockStore.runs.find((r) => r.id === runId);
  if (runListItem) runListItem.net_total = detail.summary.net_total;
  return cloneDeep(detail);
}

export function mockSubmitPayrollRun(runId) {
  const detail = mockStore.details[runId];
  if (!detail) throw new Error("Không tìm thấy kỳ lương mẫu.");
  if (!detail.payslips.length) throw new Error("Kỳ lương chưa có dữ liệu.");
  detail.run.status = "pending_coo";
  detail.run.submitted_at = new Date().toISOString();
  detail.run.current_approver_name = "Phạm COO";
  detail.approvals = detail.approvals.map((a) =>
    a.level === 1 ? { ...a, status: "waiting" } : { ...a, status: "queued" }
  );
  syncRunList(runId);
  return cloneDeep(detail);
}

export function mockApprovePayrollRun(runId) {
  const detail = mockStore.details[runId];
  if (!detail) throw new Error("Không tìm thấy kỳ lương mẫu.");
  if (detail.run.status === "pending_coo") {
    detail.run.status = "pending_ceo";
    detail.run.current_approver_name = "Hoàng CEO";
    detail.approvals = detail.approvals.map((a) => {
      if (a.level === 1) return { ...a, status: "approved", acted_at: new Date().toISOString() };
      if (a.level === 2) return { ...a, status: "waiting" };
      return a;
    });
  } else if (detail.run.status === "pending_ceo") {
    detail.run.status = "approved";
    detail.run.current_approver_name = null;
    detail.approvals = detail.approvals.map((a) =>
      a.level === 2 ? { ...a, status: "approved", acted_at: new Date().toISOString() } : a
    );
  } else {
    throw new Error("Kỳ lương không ở trạng thái chờ duyệt.");
  }
  syncRunList(runId);
  return cloneDeep(detail);
}

export function mockRejectPayrollRun(runId, reason) {
  const detail = mockStore.details[runId];
  if (!detail) throw new Error("Không tìm thấy kỳ lương mẫu.");
  const level = detail.run.status === "pending_ceo" ? 2 : 1;
  detail.run.status = "rejected";
  detail.run.current_approver_name = null;
  detail.approvals = detail.approvals.map((a) =>
    a.level === level
      ? { ...a, status: "rejected", acted_at: new Date().toISOString(), note: reason }
      : a
  );
  syncRunList(runId);
  return cloneDeep(detail);
}

export function mockPublishPayrollRun(runId) {
  const detail = mockStore.details[runId];
  if (!detail) throw new Error("Không tìm thấy kỳ lương mẫu.");
  if (detail.run.status !== "approved") throw new Error("Chỉ công bố kỳ lương đã duyệt.");
  detail.run.status = "published";
  detail.run.published_at = new Date().toISOString();
  detail.payslips = detail.payslips.map((p) => ({
    ...p,
    status: "published",
    published_at: detail.run.published_at,
  }));
  syncRunList(runId);
  return cloneDeep(detail);
}

export function mockCalculatePayrollRun(runId) {
  const detail = mockStore.details[runId];
  if (!detail) throw new Error("Không tìm thấy kỳ lương mẫu.");
  if (detail.run.status === "rejected") detail.run.status = "draft";
  detail.summary = buildRunSummary(detail.payslips);
  syncRunList(runId);
  return cloneDeep(detail);
}

function syncRunList(runId) {
  const detail = mockStore.details[runId];
  const runListItem = mockStore.runs.find((r) => r.id === runId);
  if (!detail || !runListItem) return;
  runListItem.status = detail.run.status;
  runListItem.current_approver_name = detail.run.current_approver_name;
  runListItem.payslip_count = detail.payslips.length;
  runListItem.net_total = detail.summary.net_total;
}

export function shouldUsePayrollMock(apiRecords) {
  return isPayrollMockForced() || !apiRecords || apiRecords.length === 0;
}
